import { useState } from 'react'
import { Icon } from '../icons/Icon'
import { InlineAlert } from '../InlineAlert'
import { Popover } from '../../design-system/list-view/Popover'
import { useMyJobs } from './useMyJobs'
import { JobRow } from './JobRow'
import './JobsTray.css'

const BADGE_CAP = 9

/**
 * The personal Background Jobs tray (FR-M23.11, design doc §3/§4) — global top-bar chrome, mounted
 * ONCE in `NavShell` between the Global Search trigger and the topbar spacer (AC1: every route
 * renders inside `NavShell`'s `<Outlet/>`, so topbar placement satisfies "reachable from every
 * workspace" by construction).
 *
 * Shows the calling user's OWN jobs only (`GET /background-jobs?initiated_by=me` via
 * `useMyJobs`/`listMyJobs` — a real server-side filter, never a client-side narrowing over a wider
 * read, AC9), running/queued first then recent, with gated interval polling (`usePolling`, no push
 * infra — design §4.3), cancel, error-report download, and a table-driven deep-link
 * (`resolveJobTarget`, §4.5).
 */
export function JobsTray() {
  const [open, setOpen] = useState(false)
  const { active, recent, runningCount, state, errorMessage, refetch, applyJobUpdate } = useMyJobs(open)

  const badge: number | string | undefined =
    runningCount != null && runningCount > 0 ? (runningCount > BADGE_CAP ? `${BADGE_CAP}+` : runningCount) : undefined

  const trayLabel = runningCount ? `Background jobs (${runningCount} running)` : 'Background jobs'
  const isEmpty = state === 'ready' && active.length === 0 && recent.length === 0

  return (
    <Popover
      label={trayLabel}
      triggerContent={<Icon name="activity" size={16} />}
      triggerClassName="iw-icon-btn-sm"
      open={open}
      onOpenChange={setOpen}
      align="end"
      width={420}
      badge={badge}
    >
      <div className="iw-jobs-tray">
        <div className="iw-jobs-tray__header">
          <h2 className="iw-jobs-tray__title">Background jobs</h2>
          <button
            type="button"
            className="iw-icon-btn-sm"
            onClick={refetch}
            aria-label="Refresh background jobs"
            title="Refresh"
          >
            <Icon name="refresh-cw" size={14} />
          </button>
        </div>

        {state === 'error' ? (
          <InlineAlert
            severity="error"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={refetch}>
                Try again
              </button>
            }
          >
            {errorMessage ?? 'Could not load your background jobs.'}
          </InlineAlert>
        ) : state === 'loading' ? (
          <p className="iw-jobs-tray__hint">Loading your background jobs…</p>
        ) : isEmpty ? (
          <div className="iw-jobs-tray__empty">
            <Icon name="inbox" size={24} />
            <p>No jobs yet. Anything you kick off — an import, a bulk update, an export — shows up here.</p>
          </div>
        ) : (
          <>
            {active.length > 0 ? (
              <section aria-label="Active jobs">
                <h3 className="iw-jobs-tray__section-heading">Active</h3>
                <ul className="iw-jobs-tray__list">
                  {active.map((job) => (
                    <JobRow key={job.id} job={job} onUpdated={applyJobUpdate} />
                  ))}
                </ul>
              </section>
            ) : null}
            {recent.length > 0 ? (
              <section aria-label="Recent jobs">
                <h3 className="iw-jobs-tray__section-heading">Recent</h3>
                <ul className="iw-jobs-tray__list">
                  {recent.map((job) => (
                    <JobRow key={job.id} job={job} onUpdated={applyJobUpdate} />
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </div>
    </Popover>
  )
}
